import { Link } from 'react-router-dom';
import { productShortId } from '@/lib/utils';
import type { DbProduct } from '@/hooks/use-products';
import { useBadges, useProductsRatings } from '@/hooks/use-products';
import { useCartStore } from '@/data/cart';
import { useShopPause } from '@/hooks/use-shop-pause';
import { useToast } from '@/hooks/use-toast';
import { ShoppingBag, Star } from 'lucide-react';
import SafeImage from '@/components/SafeImage';

interface Props {
  product: DbProduct & { categories?: { slug: string } | null };
}

export default function ProductCard({ product }: Props) {
  const addItem = useCartStore((s) => s.addItem);
  const { data: badges } = useBadges();
  const { data: ratings } = useProductsRatings();
  const { data: pauseState } = useShopPause();
  const { toast } = useToast();
  const rating = ratings?.[product.id];

  const badge = product.badge && badges?.find((b) => b.slug === product.badge);
  const isSoldOut = product.stock_quantity != null && product.stock_quantity === 0;
  const shopPaused = !!pauseState?.paused;

  // Pick black or white text for badge based on WCAG contrast ratio
  const badgeTextColor = (() => {
    if (!badge?.color) return '#ffffff';
    const hex = badge.color.replace('#', '');
    if (hex.length !== 6) return '#ffffff';
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    const toLin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
    const L = 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
    const contrastWhite = 1.05 / (L + 0.05);
    const contrastBlack = (L + 0.05) / 0.05;
    return contrastBlack >= contrastWhite ? '#111111' : '#ffffff';
  })();

  return (
    <article className="group">
      <div className="relative">
        <Link
          to={`/produkt/${productShortId(product.slug)}`}
          onClick={() => { try { sessionStorage.setItem('shop-scroll-y', String(window.scrollY)); } catch { /* ignore */ } }}
          className="block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-label={`${product.name} ansehen`}
        >
          <div className="relative aspect-square overflow-hidden rounded-lg bg-secondary">
            <SafeImage
              src={product.image || ''}
              alt={product.name}
              width={600}
              height={600}
              wrapperClassName="absolute inset-0 h-full w-full"
              className={`h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 ${isSoldOut ? 'opacity-60' : ''}`}
              loading="lazy"
              decoding="async"
            />
            {badge && (
              <span
                className="absolute top-2 left-2 sm:top-3 sm:left-3 rounded-full px-2 py-1 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-semibold shadow-md max-w-[75%] truncate"
                style={{ backgroundColor: badge.color, color: badgeTextColor }}
              >
                {badge.name}
              </span>
            )}
            {isSoldOut && (
              <span className="absolute top-2 right-2 sm:top-3 sm:right-3 rounded-full bg-destructive px-2 py-1 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-semibold text-destructive-foreground shadow-md">
                Ausverkauft
              </span>
            )}
          </div>
        </Link>
        {!isSoldOut && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (shopPaused) {
                toast({
                  title: 'Shop ist gerade pausiert',
                  description: 'Bestellungen sind in Kürze wieder möglich.',
                });
                return;
              }
              addItem({
                id: product.id,
                name: product.name,
                price: Number(product.price),
                original_price: product.original_price ? Number(product.original_price) : null,
                image: product.image,
                slug: product.slug,
              });
            }}
            aria-label={shopPaused ? `${product.name} – Shop pausiert` : `${product.name} in den Warenkorb legen`}
            aria-disabled={shopPaused}
            className={`absolute bottom-2 right-2 inline-flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100 transition-all duration-300 md:hover:scale-110 outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 ${shopPaused ? 'opacity-60 md:opacity-60 md:group-hover:opacity-60 cursor-not-allowed' : ''}`}
          >
            <ShoppingBag className="h-[18px] w-[18px]" aria-hidden="true" />
          </button>
        )}
      </div>
      <Link
        to={`/produkt/${productShortId(product.slug)}`}
        onClick={() => { try { sessionStorage.setItem('shop-scroll-y', String(window.scrollY)); } catch { /* ignore */ } }}
        className="block mt-2.5 sm:mt-3 space-y-1 outline-none focus-visible:ring-2 focus-visible:ring-primary rounded py-1 -my-1"
        tabIndex={-1}
      >
        <h3 className="text-[13px] sm:text-sm font-medium leading-snug line-clamp-2 min-h-[2.4rem] sm:min-h-[2.5rem] text-foreground group-hover:text-primary transition-colors">
          {product.name}
        </h3>
        {rating && rating.count > 0 && (
          <div className="flex items-center gap-1 text-xs text-foreground/70">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            <span className="font-semibold text-foreground">{rating.avg.toFixed(1)}</span>
            <span>({rating.count})</span>
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-heading font-semibold text-[15px] sm:text-base text-foreground">
            {Number(product.price).toFixed(2).replace('.', ',')}€
          </span>
          {product.original_price && Number(product.original_price) > Number(product.price) && (
            <>
              <span className="text-xs sm:text-sm text-foreground/75 line-through">
                {Number(product.original_price).toFixed(2).replace('.', ',')}€
              </span>
              <span className="text-xs font-bold text-destructive">
                −{Math.round((1 - Number(product.price) / Number(product.original_price)) * 100)}%
              </span>
            </>
          )}
        </div>
      </Link>
    </article>
  );
}
